import { useLayoutEffect, useRef, type RefObject } from "react";
import { useLocation } from "react-router-dom";

import { useSidebarNavState } from "@/navigation/sidebarRegistry";
import type { SidebarId } from "@/navigation/types";

const ACTIVE_ITEM_PADDING_PX = 8;

const sidebarScrollById = new Map<SidebarId, number>();

function scrollChildIntoContainer(container: HTMLElement, child: HTMLElement): void {
  const containerRect = container.getBoundingClientRect();
  const childRect = child.getBoundingClientRect();

  if (childRect.top < containerRect.top + ACTIVE_ITEM_PADDING_PX) {
    container.scrollTop -= containerRect.top + ACTIVE_ITEM_PADDING_PX - childRect.top;
    return;
  }

  if (childRect.bottom > containerRect.bottom - ACTIVE_ITEM_PADDING_PX) {
    container.scrollTop += childRect.bottom - (containerRect.bottom - ACTIVE_ITEM_PADDING_PX);
  }
}

/**
 * Keeps the desktop sidebar rail at the last scroll offset for this nav scope.
 * Fullscreen form routes unmount the app shell, so without this the rail jumps back to the top.
 */
export function useSidebarNavScroll(): RefObject<HTMLElement | null> {
  const ref = useRef<HTMLElement>(null);
  const lastScrollRef = useRef(0);
  const { pathname } = useLocation();
  const { sidebarId, isLoading } = useSidebarNavState();

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || isLoading) return;

    const saved = sidebarScrollById.get(sidebarId);
    if (saved != null) {
      el.scrollTop = saved;
      lastScrollRef.current = saved;
    }

    const active = el.querySelector<HTMLElement>('[aria-current="page"]');
    if (active) scrollChildIntoContainer(el, active);

    lastScrollRef.current = el.scrollTop;
    sidebarScrollById.set(sidebarId, lastScrollRef.current);

    const persist = (): void => {
      lastScrollRef.current = el.scrollTop;
      sidebarScrollById.set(sidebarId, lastScrollRef.current);
    };

    el.addEventListener("scroll", persist, { passive: true });
    return () => {
      sidebarScrollById.set(sidebarId, lastScrollRef.current);
      el.removeEventListener("scroll", persist);
    };
  }, [isLoading, pathname, sidebarId]);

  return ref;
}
